/**
 * Module dependencies.
 */

import { Button, Typography, css } from '@mui/material';
import { Image as ImageComponent } from 'components/atoms/image/image';
import { SubTitle } from 'components/atoms/sub-title/sub-title';
import { color } from 'styles/utils/colors';
import { ifProp } from 'styled-tools';
import styled from '@emotion/styled';

/**
 * Export `Wrapper` styled component.
 */

export const Wrapper = styled.div`
  flex-direction: row;
  display: flex;
  gap: 32px;
  margin-top: 40px;
`;

/**
 * Export `Content` styled component.
 */

export const Content = styled.div<{
  noCharacteristics: boolean;
}>`
  flex: 0.8;
  flex-direction: row;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: min-content min-content;
  grid-template-areas:
    'image characteristics'
    'technicalImage info';
  align-items: start;
  column-gap: 60px;
  row-gap: 0px;

  ${ifProp(
    'noCharacteristics',
    css`
      grid-template-areas:
        'image info'
        'technicalImage info';
    `,
  )}

  @media (max-width: 1200px) {
    flex: 1;
  }

  @media (max-width: 680px) {
    grid-template-columns: 0.3fr 1fr 0.3fr;
    grid-template-areas:
      '. image .'
      'characteristics characteristics characteristics'
      '. technicalImage .'
      'info info info';
  }

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'image'
      'characteristics'
      'technicalImage'
      'info';
  }
`;

/**
 * Export `Image` styled component.
 */

export const Image = styled(ImageComponent)<{
  area: string;
}>`
  grid-area: ${(props) => props.area};
  width: 100%;
  align-self: start;
  padding: 16px;
`;

/**
 * Export `StyledSubTitle` styled component.
 */

export const StyledSubTitle = styled(SubTitle)<{
  small?: boolean;
}>`
  color: ${color('primary400')};
  margin-bottom: 16px;

  ${ifProp(
    'small',
    css`
      font-size: 16px;
    `,
  )}
`;

/**
 * Export `CharacteristicsWrapper` styled component.
 */

export const CharacteristicsWrapper = styled.div`
  grid-area: characteristics;
`;

/**
 * Export `Info` styled component.
 */

export const Info = styled.div`
  grid-area: info;
`;

/**
 * Export `Characteristics` styled component.
 */

export const Characteristics = styled(Typography)`
  white-space: pre-line;
  width: 100%;
  font-size: 13px;
`;

/**
 * Export `Label` styled component.
 */

export const Label = styled(Typography)<{ bold?: boolean }>`
  font-size: 13px;
  color: ${color('primary700')};
  font-weight: ${ifProp('bold', 'bold', 'normal')};
`;

/**
 * Export `TechnicalInformationWrapper` styled component.
 */

export const TechnicalInformationWrapper = styled.div`
  background-color: ${color('primary100')};
`;

/**
 * Export `Line` styled component.
 */

export const Line = styled.div<{ colorOdd: boolean }>`
  display: flex;
  justify-content: space-between;
  flex: 1;
  padding: 12px 16px;

  ${({ theme }) =>
    ifProp(
      'colorOdd',
      css`
        background-color: ${color('primary200')({ theme })};
      `,
    )}
`;

/**
 * Export `DownloadWrapper` styled component.
 */

export const DownloadWrapper = styled.div`
  margin-top: 40px;
`;

/**
 * Export `StyledButton` styled component.
 */

export const StyledButton = styled(Button)`
  font-family: 'Inter', sans-serif;
`;
